import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "@effect/platform";
import * as S from "effect/Schema";
import { ContractType } from "./admin-clients.js";
import { FileRow } from "./files.js";
import { NotFound } from "./folders.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
/**
 * The admin document library's file CRUD — cross-client (any `client_id`,
 * or `client_id IS NULL` for the admin's own global library), unlike
 * `files.*`'s own-client-only scope. Authorization mirrors
 * `admin-clients`/`admin-locations`: `isUnrestrictedInternal` for a global
 * file, `canManageClient` for a client-owned one.
 */
export const ListAdminFilesParams = S.extend(PagingParams, S.Struct({
    folder_id: S.optional(S.UUID),
    client_id: S.optional(S.UUID),
    search: S.optional(S.String),
}));
/**
 * `folder_id`/`client_id` both omitted reads the admin's global root
 * (`folder IS NULL AND client_id IS NULL`).
 */
export const ListAdminFiles = HttpApiEndpoint.get("listAdminFiles", "/admin/files")
    .setUrlParams(ListAdminFilesParams)
    .addSuccess(pagedResponse(FileRow))
    .addError(Forbidden, { status: 403 });
/**
 * Upload metadata rides `urlParams` (the assets package's own
 * `uploadAssetFile` convention, matching `files.uploadFile`) — `client_id`
 * omitted stores a global admin file (`visibility_scope: "admin"`); given,
 * a client-owned one (`visibility_scope: "customer"`), mirroring
 * `FilesService.validateCustomerFileIsolation`'s admin-caller branch. This
 * intent never resolves `"public"` — that folder-specific carve-out belongs
 * to the branding/logo upload path, not admin content management.
 */
export const UploadAdminFileParams = S.Struct({
    filename: S.NonEmptyString,
    content_type: S.NonEmptyString,
    folder_id: S.optional(S.UUID),
    client_id: S.optional(S.UUID),
});
export const UploadAdminFile = HttpApiEndpoint.post("uploadAdminFile", "/admin/files/upload")
    .setUrlParams(UploadAdminFileParams)
    .setPayload(HttpApiSchema.Uint8Array())
    .addSuccess(S.Struct({ data: FileRow }))
    .addError(Forbidden, { status: 403 });
/**
 * Partial patch over the writable `yp_files` columns the upsert-file
 * modal's edit flow needs — metadata only, never `storage`/`filename_disk`
 * (re-uploading bytes is a delete-then-upload, not an update). `folder_id`
 * omitted leaves the file where it is; `null` moves it to the caller's
 * global/client root the same way `folder_id` omitted on `ListAdminFiles`
 * reads it.
 */
export const UpdateAdminFilePayload = S.Struct({
    id: S.UUID,
    filename_download: S.optional(S.NonEmptyString),
    title: S.optional(S.NullOr(S.String)),
    description: S.optional(S.NullOr(S.String)),
    folder_id: S.optional(S.NullOr(S.UUID)),
    contract_types: S.optional(S.NullOr(S.Array(ContractType))),
});
export const UpdateAdminFile = HttpApiEndpoint.post("updateAdminFile", "/admin/files/update")
    .setPayload(UpdateAdminFilePayload)
    .addSuccess(S.Struct({ data: FileRow }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteAdminFilePayload = S.Struct({ id: S.UUID });
export const DeleteAdminFile = HttpApiEndpoint.post("deleteAdminFile", "/admin/files/delete")
    .setPayload(DeleteAdminFilePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * Bulk delete — silently skips any id the caller can't manage or that
 * doesn't exist rather than 404ing the whole batch, matching
 * `admin-sharing`'s tolerant bulk-write style; `deleted_count` tells the
 * caller how many of the requested ids actually went.
 */
export const DeleteManyAdminFilesPayload = S.Struct({
    ids: S.NonEmptyArray(S.UUID),
});
export const DeleteManyAdminFiles = HttpApiEndpoint.post("deleteManyAdminFiles", "/admin/files/delete-many")
    .setPayload(DeleteManyAdminFilesPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted_count: S.Int }) }))
    .addError(Forbidden, { status: 403 });
/**
 * Raw bytes for one admin-visible `yp_files` row, with an optional resize
 * transform applied when the stored file is an image
 * (`SUPPORTED_IMAGE_TRANSFORM_FORMATS`) — non-image files and files with no
 * transform params ignore these fields and return the stored bytes
 * verbatim. This is a direct width/height/quality/format/fit translation,
 * not the legacy Directus preset-key/on-disk-render-cache engine
 * (`ASSET_TRANSFORM_QUERY_KEYS`) — that indirection has no admin-content
 * use case this contract serves yet.
 */
export const ImageFit = S.Literal("cover", "contain", "inside", "outside");
export const ImageFormat = S.Literal("jpg", "png", "webp", "avif", "tiff");
export const DownloadAdminFileParams = S.Struct({
    id: S.UUID,
    width: S.optional(S.NumberFromString.pipe(S.int(), S.between(1, 10_000))),
    height: S.optional(S.NumberFromString.pipe(S.int(), S.between(1, 10_000))),
    quality: S.optional(S.NumberFromString.pipe(S.int(), S.between(1, 100))),
    format: S.optional(ImageFormat),
    fit: S.optional(ImageFit),
});
export const DownloadAdminFile = HttpApiEndpoint.get("downloadAdminFile", "/admin/files/download")
    .setUrlParams(DownloadAdminFileParams)
    .addSuccess(HttpApiSchema.Uint8Array({ contentType: "application/octet-stream" }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * The public-folder branding/profile assets (client logos, internal-user
 * profile pictures) live under the instance-wide public folder
 * (`yp_settings.storage_default_folder`) with `visibility_scope: "public"`.
 * The caller never names a folder — the server derives it from settings, so
 * a client-supplied folder id can't smuggle a file into another scope.
 */
export const UploadPublicFileParams = S.Struct({
    filename: S.NonEmptyString,
    content_type: S.NonEmptyString,
});
/** `yp_settings.storage_default_folder` is unset — there is no public folder to store into. */
export const PublicFolderNotConfigured = S.Struct({
    _tag: S.Literal("PublicFolderNotConfigured"),
});
export const UploadPublicFile = HttpApiEndpoint.post("uploadPublicFile", "/admin/files/upload-public")
    .setUrlParams(UploadPublicFileParams)
    .setPayload(HttpApiSchema.Uint8Array())
    .addSuccess(S.Struct({ data: FileRow }))
    .addError(PublicFolderNotConfigured, { status: 422 })
    .addError(Forbidden, { status: 403 });
export const DownloadPublicFileParams = S.Struct({ id: S.UUID });
/**
 * Raw bytes for one public-folder file — readable by any authenticated
 * principal (the legacy public-asset semantics). A row outside the public
 * folder is the same `NotFound` as a missing id: this intent never confirms
 * whether a non-public file id exists.
 */
export const DownloadPublicFile = HttpApiEndpoint.get("downloadPublicFile", "/admin/files/download-public")
    .setUrlParams(DownloadPublicFileParams)
    .addSuccess(HttpApiSchema.Uint8Array({ contentType: "application/octet-stream" }))
    .addError(NotFound, { status: 404 });
export const DeletePublicFilePayload = S.Struct({ id: S.UUID });
/**
 * Removes one public-folder file and its storage bytes — a row outside the
 * public folder is `NotFound`, so this intent can never be used to delete
 * admin/customer-scoped content.
 */
export const DeletePublicFile = HttpApiEndpoint.post("deletePublicFile", "/admin/files/delete-public")
    .setPayload(DeletePublicFilePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(NotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const adminFilesGroup = HttpApiGroup.make("admin-files")
    .add(ListAdminFiles)
    .add(UploadAdminFile)
    .add(UpdateAdminFile)
    .add(DeleteAdminFile)
    .add(DeleteManyAdminFiles)
    .add(DownloadAdminFile)
    .add(UploadPublicFile)
    .add(DownloadPublicFile)
    .add(DeletePublicFile)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-files.js.map